import {onMounted, onUnmounted, Ref, ref} from "vue";

/** Sets event listeners for each type of mouse event so that unexpected
 * behavior caused by the `vue3-carousel` component is avoided.
 * The way this is done is by exposing a `Ref<boolean>` that will always
 * tell if the given tag(s) is(are) allowed to perform a click safely without
 * messing up with the carousel dragging behavior.
 * @param className CSS class of the element(s) you want to handle the clicks. */
export const useCarouselMouseEvents = (
  className: string
): {
  isAllowedToClick: Ref<boolean>;
} => {
  const isMouseDown: Ref<boolean> = ref(false);
  const isDragging: Ref<boolean> = ref(false);
  const isAllowedToClick: Ref<boolean> = ref(false);

  const onMouseDown = (): void => {
    isAllowedToClick.value = false;
    isMouseDown.value = true;
  };

  const onMouseMove = (): void => {
    isDragging.value = isMouseDown.value;
  };

  const onMouseUp = async (): Promise<void> => {
    isAllowedToClick.value = !isDragging.value;
    isMouseDown.value = false;
    isDragging.value = false;
  };

  onMounted(() => {
    const elements = document.getElementsByClassName(className);
    for (let element of elements) {
      element.addEventListener("mousedown", onMouseDown);
      element.addEventListener("mousemove", onMouseMove);
      element.addEventListener("mouseup", onMouseUp);
      element.addEventListener("touchstart", onMouseDown);
      element.addEventListener("touchmove", onMouseMove);
      element.addEventListener("touchend", onMouseUp);
    }
  });

  onUnmounted(() => {
    const elements = document.getElementsByClassName(className);
    for (let element of elements) {
      element.removeEventListener("mousedown", onMouseDown);
      element.removeEventListener("mousemove", onMouseMove);
      element.removeEventListener("mouseup", onMouseUp);
      element.removeEventListener("touchstart", onMouseDown);
      element.removeEventListener("touchmove", onMouseMove);
      element.removeEventListener("touchend", onMouseUp);
    }
  });

  return { isAllowedToClick };
};
